import { DimensionField } from "components/ui/customWebviewer/FurnitureSidebar/layout/DimensionsSection/layout/DimensionField/DimensionField"
import style from "./DimensionsSection.module.scss"

const { section, sectionTitle, inputs } = style

type Props = {
	width: number | null
	height: number | null
	depth: number | null
	onWidthChange: (value: number | null) => void
	onHeightChange: (value: number | null) => void
	onDepthChange: (value: number | null) => void
	onWidthCommit?: () => void
	onHeightCommit?: () => void
	onDepthCommit?: () => void
	isRequired?: boolean
	/**
	 * Smallest accepted value. The edit sidebar keeps 0 (an existing object may legitimately carry
	 * one); the create panels pass 1, where a zero dimension can only produce a degenerate object.
	 *
	 * Every endpoint that takes a dimension — the single-object PATCH, the batch PATCH and the create
	 * POSTs — types width / height / depth as `integer, minimum: 0`, so a typed `40.5` could only ever
	 * be rejected. `DimensionField` therefore validates the typed text and sends nothing while it is
	 * invalid (issue #1088); it does not round or clamp. That matters most in the batch panel, where
	 * one bad value rejects the whole selection.
	 */
	min?: number
	/**
	 * Per-axis placeholder, shown while that input is empty. The batch panel (REQ-403) puts `Mixed`
	 * in the axes whose values differ across the selection: the field really is empty there — typing
	 * a value is what applies one to every object, and leaving it alone omits it from the request.
	 */
	placeholders?: { width?: string; height?: string; depth?: string }
	/**
	 * The `n LnFt` hint under Width. The batch panel hides it unless all three dimensions are
	 * shared — a linear-feet figure derived from one axis of a partly-Mixed selection would name a
	 * quantity none of the objects has.
	 */
	showLnFt?: boolean
}

const toLnFt = (inches: number | null): string | null => {
	if (inches === null || Number.isNaN(inches)) return null
	return `${Math.round(inches / 12)} LnFt`
}

export const DimensionsSection = ({
	width,
	height,
	depth,
	onWidthChange,
	onHeightChange,
	onDepthChange,
	onWidthCommit,
	onHeightCommit,
	onDepthCommit,
	isRequired = true,
	min = 0,
	placeholders,
	showLnFt = true,
}: Props) => {
	const widthLnFt = showLnFt ? toLnFt(width) : null

	return (
		<div className={section}>
			<p className={sectionTitle}>Dimensions</p>
			<div className={inputs}>
				<DimensionField
					label="Width"
					value={width}
					onChange={onWidthChange}
					onCommit={onWidthCommit}
					isRequired={isRequired}
					min={min}
					placeholder={placeholders?.width}
					hint={widthLnFt}
				/>
				<DimensionField
					label="Height"
					value={height}
					onChange={onHeightChange}
					onCommit={onHeightCommit}
					isRequired={isRequired}
					min={min}
					placeholder={placeholders?.height}
				/>
				<DimensionField
					label="Depth"
					value={depth}
					onChange={onDepthChange}
					onCommit={onDepthCommit}
					isRequired={isRequired}
					min={min}
					placeholder={placeholders?.depth}
				/>
			</div>
		</div>
	)
}
